import { ValidationError, normalizePhone } from '@selogate/core'
import { useTranslation } from '../../i18n/I18nProvider'
import { PrimaryButton, SecondaryButton } from '../Button'
import { AuthError } from './AuthError'
import type { PhoneStepProps } from './types'

/**
 * Client-side enablement only, reusing `@selogate/core`'s own `normalizePhone` (§11) rather
 * than a forked format check — never a second implementation of the same rule. The 422 that
 * actually decides remains server-side; a phone that passes this check can still be refused.
 *
 * **R1-5(3) — nothing thrown here may reach React.** This runs *during render*, at the front
 * door, before any session exists. `sendCode`'s identical call is already guarded
 * (`AuthProvider.tsx`, "not a rejected number — the normalizer itself failed"); this one was
 * not, so a non-`ValidationError` throw unmounted the whole app to a blank document on the very
 * first screen — the one place where there is no drawer, no back, and no session to sign out of.
 *
 * A `ValidationError` *is* the answer: not sendable yet. Anything else is a bug in the
 * normalizer, and the honest response to a bug is a disabled button and a logged error, not a
 * white screen. The boundary in `App.tsx` is the second line of defence, not this fix — and it
 * does not even cover the `auth` route, which renders outside it.
 */
function looksSendable(phone: string): boolean {
  try {
    normalizePhone(phone, 'IL')
    return true
  } catch (error) {
    if (!(error instanceof ValidationError)) console.error('phone-normalize-failed', error)
    return false
  }
}

export function PhoneStep({
  phone,
  onChangePhone,
  error,
  retryAt,
  resendInSeconds,
  onSubmit,
  submitting,
  onRetryRestore,
}: PhoneStepProps): JSX.Element {
  const { t } = useTranslation('auth')
  const { t: tCommon } = useTranslation('common')
  const canSubmit = !submitting && looksSendable(phone)

  return (
    <form
      onSubmit={(event) => {
        event.preventDefault()
        if (canSubmit) onSubmit()
      }}
    >
      <div style={{ padding: '36px 16px 4px', textAlign: 'center' }}>
        <div style={{ fontSize: 'var(--font-header)', fontWeight: 700, marginBlockEnd: 6 }}>
          {t('phoneTitle')}
        </div>
        <div style={{ fontSize: 'var(--font-control)', color: 'var(--muted)' }}>{t('phoneSubtitle')}</div>
      </div>
      {/* The boot could not read the phone's storage, which is not the same as finding it empty:
          the session may still be there. Said first, with the one action that can recover it
          without an SMS; the form stays below for a resident who would rather sign in. */}
      {onRetryRestore === undefined ? null : (
        <div className="form-row" role="alert">
          <p className="hint centered">{t('storageUnreadable')}</p>
          <SecondaryButton onClick={onRetryRestore}>{tCommon('retry')}</SecondaryButton>
        </div>
      )}
      <div className="form-row">
        <label htmlFor="auth-phone">{t('phoneLabel')}</label>
        <input
          id="auth-phone"
          className="input"
          type="tel"
          inputMode="tel"
          autoComplete="tel"
          dir="ltr"
          value={phone}
          placeholder={t('phonePlaceholder')}
          onChange={(event) => onChangePhone(event.target.value)}
          aria-label={t('phoneLabel')}
        />
      </div>
      <AuthError error={error} retryAt={retryAt} resendInSeconds={resendInSeconds} />
      {/* Wrapped in `.form-row` (fix A) so the button's inline inset matches the input's above
          it — `.btn-primary` alone is edge-to-edge and bled past the input's 16px inset. */}
      <div className="form-row">
        <PrimaryButton type="submit" disabled={!canSubmit}>
          {t('sendCode')}
        </PrimaryButton>
      </div>
    </form>
  )
}
